import React from 'react';
import { FormattedMessage } from 'react-intl';
import Styled from 'styled';
import classnames from 'classnames';
import { Mutation } from '@apollo/client/react/components';

import SVGIcon from 'lib/images/svg-icon/svg-icon';

import Checkbox from 'four-nets/ui-v2/form/checkbox/checkbox';
import settings from 'four-nets/icons/svg/icon_settings.svg';
import LoadingWrapperView from 'four-nets/ui/loaders/loading-wrapper-view';
import { AlertContext } from 'lib/alert-context/alert-context';

import { MySelectionLoaderQuery } from './my-selection.graphql';
import updateSelectionMutation, {
    UpdateSelectionMutation,
    UpdateSelectionMutationVariables,
    SelectionItemInput,
} from './mutations/update-selection.graphql';
import defaultStyles from './my-selection.scss';

export interface Props {
    styles: typeof defaultStyles;
    refetchQueries: string[];
    selectionItems: NonNullable<MySelectionLoaderQuery['selectionItems']>;
}

interface State {
    selectionItemValues: Map<string, boolean>;
    touched: boolean;
    loading: boolean;
    open: boolean;
}

class MySelection extends React.PureComponent<Props, State> {
    constructor(props: Props) {
        super(props);
        const selectionItemValues: Map<string, boolean> = new Map();

        const { selectionItems } = props;
        selectionItems.edges!.forEach(edge => {
            if (edge && edge.node) {
                selectionItemValues.set(edge.node.id, edge.node.isSelectedByViewer || false);
            }
        });

        this.state = {
            selectionItemValues,
            touched: false,
            loading: false,
            open: false,
        };
    }

    onChange = (id: string) => () => {
        this.setState(({ selectionItemValues }) => {
            const newSelectionItemValues = new Map(selectionItemValues);
            newSelectionItemValues.set(id, !newSelectionItemValues.get(id));
            return {
                selectionItemValues: newSelectionItemValues,
                touched: true,
            };
        });
    };

    transformToInput = () => {
        const result: SelectionItemInput[] = [];
        this.state.selectionItemValues.forEach((selected, id) => {
            result.push({ id, selected });
        });
        return result;
    };

    render() {
        const { styles, selectionItems, refetchQueries } = this.props;
        return (
            <div className={styles.wrapper}>
                <LoadingWrapperView loading={this.state.loading}>
                    <div className={styles.header}>
                        <div className={styles.title}>
                            <SVGIcon style={{ image: styles.icon }} src={settings} />
                            <FormattedMessage id='MY_SELECTION_SETTINGS' />
                        </div>
                        <button
                            className={styles.openButton}
                            onClick={() => {
                                this.setState(prevState => ({ open: !prevState.open }));
                            }}
                        >
                            <FormattedMessage id='EDIT' values={{ gender: 'v' }} />
                        </button>
                    </div>
                    <div className={classnames(styles.contentWrapper, { [styles.open]: this.state.open })}>
                        <div className={styles.description}>
                            <FormattedMessage id='MY_SELECTION_DESCRIPTION' />
                        </div>
                        <ul className={styles.selectionList}>
                            {selectionItems.edges.map(edge => {
                                if (edge && edge.node) {
                                    return (
                                        <li className={styles.selectionItem} key={edge.node.name || ''}>
                                            <label>
                                                <Checkbox
                                                    style={{ checkboxInput: styles.checkboxInput }}
                                                    id={edge.node.name || undefined}
                                                    value={this.state.selectionItemValues.get(edge.node.id)}
                                                    onChange={this.onChange(edge.node.id)}
                                                />
                                                {edge.node.name}
                                            </label>
                                        </li>
                                    );
                                }
                                return null;
                            })}
                        </ul>
                        <Mutation<UpdateSelectionMutation, UpdateSelectionMutationVariables>
                            mutation={updateSelectionMutation}
                            refetchQueries={refetchQueries}
                        >
                            {updateSelection => (
                                <AlertContext.Consumer>
                                    {({ retry }) => {
                                        const onAccept: () => any = () => {
                                            this.setState({ loading: true });
                                            return updateSelection({
                                                variables: { selectionItems: this.transformToInput() },
                                            })
                                                .then(() =>
                                                    this.setState({ touched: false, loading: false, open: false })
                                                )
                                                .catch(err => retry({ onAccept, body: err.message }));
                                        };

                                        return (
                                            <button
                                                disabled={!this.state.touched}
                                                className={styles.saveButton}
                                                onClick={onAccept}
                                            >
                                                <FormattedMessage id='SAVE_SETTINGS' />
                                            </button>
                                        );
                                    }}
                                </AlertContext.Consumer>
                            )}
                        </Mutation>
                    </div>
                </LoadingWrapperView>
            </div>
        );
    }
}

export default Styled(defaultStyles)(MySelection);
